import { apiService } from '../../../components/api/apiService';
import { logLlmGateway } from './llmGatewayLogger';
import { normalizeGovernance } from './llmGatewayConstants';
import type {
  GatewayFormState,
  GatewayIntent,
  GatewayStatus,
  GovernanceSettings,
  IntentGatewayEvents,
  IntentGatewayStatistics,
  IntentRoutingConfig,
  LlmGateway,
  RegisteredLlm,
} from './llmGatewayTypes';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/llm-gateway';
const STATS_BASE = '/api/v1/reporting/llm-gateway';

function serviceErrorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string }; message?: string }; status?: number };
    message?: string;
  };
  if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message === 'Network Error') {
    return 'Cannot reach BackendPYApp at http://127.0.0.1:8014. Start it with `python run.py` from BackendPYApp.';
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'LLM Gateway request failed'
  );
}

function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  return [];
}

function normalizeIntentRouting(raw: IntentRoutingConfig | null | undefined): IntentRoutingConfig {
  return {
    default_model: String(raw?.default_model || ''),
    cache: {
      enabled: Boolean(raw?.cache?.enabled),
      type: 'exact',
      ttl_seconds: Number.isFinite(raw?.cache?.ttl_seconds) ? Number(raw?.cache?.ttl_seconds) : 3600,
    },
    intents: asArray<GatewayIntent>(raw?.intents).map((item) => ({
      id: String(item.id || ''),
      name: String(item.name || ''),
      description: String(item.description || ''),
      examples: asArray<string>(item.examples),
      target_model: String(item.target_model || ''),
      enabled: item.enabled !== false,
      config_version: item.config_version,
    })),
  };
}

function normalizeGateway(raw: Record<string, unknown>): LlmGateway {
  return {
    id: String(raw.id || ''),
    name: String(raw.name || ''),
    llmType: raw.llmType as LlmGateway['llmType'],
    modelId: String(raw.modelId || ''),
    provider: raw.provider as LlmGateway['provider'],
    apiTypes: asArray<LlmGateway['apiTypes'][number]>(raw.apiTypes),
    customEndpoint: String(raw.customEndpoint || ''),
    authMethod: (raw.authMethod as LlmGateway['authMethod']) || 'api_key',
    governance: normalizeGovernance(raw.governance as GovernanceSettings),
    status: (raw.status as GatewayStatus) || 'draft',
    publicAlias: String(raw.publicAlias || ''),
    routeKey: String(raw.routeKey || ''),
    createdAt: String(raw.createdAt || ''),
    updatedAt: String(raw.updatedAt || ''),
    hasApiKey: Boolean(raw.hasApiKey),
    apiKeyLast4: typeof raw.apiKeyLast4 === 'string' ? raw.apiKeyLast4 : null,
    hasInferenceKey: Boolean(raw.hasInferenceKey),
    inferenceKeyLast4: typeof raw.inferenceKeyLast4 === 'string' ? raw.inferenceKeyLast4 : null,
  };
}

export const llmGatewayApi = {
  async listModels(): Promise<RegisteredLlm[]> {
    logLlmGateway('app', 'models_load');
    try {
      const data = (await apiService.get(`${BASE}/models`, {}, CONNECTION)) as { items?: RegisteredLlm[] };
      const items = asArray<RegisteredLlm>(data.items);
      logLlmGateway('app', 'models_load_ok', { count: items.length });
      return items;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'models_load_failed', { message });
      throw new Error(message);
    }
  },

  async listGateways(): Promise<LlmGateway[]> {
    logLlmGateway('app', 'gateways_load');
    try {
      const data = (await apiService.get(`${BASE}/gateways`, {}, CONNECTION)) as { items?: Record<string, unknown>[] };
      const items = asArray<Record<string, unknown>>(data.items).map(normalizeGateway);
      logLlmGateway('app', 'gateways_load_ok', { count: items.length });
      return items;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'gateways_load_failed', { message });
      throw new Error(message);
    }
  },

  async saveGateway(form: GatewayFormState, options: { id?: string; status: GatewayStatus }): Promise<LlmGateway> {
    const payload = {
      name: form.name.trim(),
      llmType: form.llmType,
      modelId: form.modelId,
      apiTypes: form.apiTypes,
      customEndpoint: form.customEndpoint.trim(),
      authMethod: form.authMethod,
      governance: form.governance,
      status: options.status,
    };
    try {
      const raw = options.id
        ? ((await apiService.put(`${BASE}/gateways/${options.id}`, payload, CONNECTION)) as Record<string, unknown>)
        : ((await apiService.post(`${BASE}/gateways`, payload, CONNECTION)) as Record<string, unknown>);
      logLlmGateway('app', options.id ? 'gateway_updated' : 'gateway_created', { id: raw.id, status: options.status });
      return normalizeGateway(raw);
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'gateway_save_failed', { message, id: options.id });
      throw new Error(message);
    }
  },

  async updateStatus(id: string, status: GatewayStatus): Promise<LlmGateway> {
    try {
      const raw = (await apiService.patch(`${BASE}/gateways/${id}/status`, { status }, CONNECTION)) as Record<string, unknown>;
      logLlmGateway('app', 'gateway_status', { id, status });
      return normalizeGateway(raw);
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'gateway_status_failed', { message, id });
      throw new Error(message);
    }
  },

  async updateGovernance(id: string, governance: GovernanceSettings): Promise<LlmGateway> {
    try {
      const raw = (await apiService.patch(`${BASE}/gateways/${id}/governance`, governance, CONNECTION)) as Record<string, unknown>;
      logLlmGateway('app', 'governance_updated', { id });
      return normalizeGateway(raw);
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'governance_update_failed', { message, id });
      throw new Error(message);
    }
  },

  async deleteGateway(id: string): Promise<void> {
    try {
      await apiService.delete(`${BASE}/gateways/${id}`, CONNECTION);
      logLlmGateway('warning', 'gateway_deleted', { id });
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'gateway_delete_failed', { message, id });
      throw new Error(message);
    }
  },

  async getIntentRouting(gatewayId: string): Promise<IntentRoutingConfig> {
    try {
      const data = (await apiService.get(`${BASE}/gateways/${gatewayId}/intent-routing`, {}, CONNECTION)) as IntentRoutingConfig;
      const config = normalizeIntentRouting(data);
      logLlmGateway('app', 'intent_routing_loaded', { gatewayId, intents: config.intents.length });
      return config;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'intent_routing_load_failed', { gatewayId, message });
      throw new Error(message);
    }
  },

  async saveIntentDefaults(
    gatewayId: string,
    payload: Pick<IntentRoutingConfig, 'default_model' | 'cache'>
  ): Promise<IntentRoutingConfig> {
    try {
      const data = (await apiService.put(
        `${BASE}/gateways/${gatewayId}/intent-routing`,
        payload,
        CONNECTION
      )) as IntentRoutingConfig;
      const config = normalizeIntentRouting(data);
      logLlmGateway('app', 'intent_routing_defaults_saved', { gatewayId });
      return config;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'intent_routing_defaults_failed', { gatewayId, message });
      throw new Error(message);
    }
  },

  async saveIntent(
    gatewayId: string,
    intent: Omit<GatewayIntent, 'id' | 'config_version'> & { id?: string }
  ): Promise<IntentRoutingConfig> {
    const payload = {
      name: intent.name,
      description: intent.description,
      examples: intent.examples,
      target_model: intent.target_model,
      enabled: intent.enabled,
    };
    try {
      const data = intent.id
        ? ((await apiService.put(
            `${BASE}/gateways/${gatewayId}/intent-routing/intents/${intent.id}`,
            payload,
            CONNECTION
          )) as IntentRoutingConfig)
        : ((await apiService.post(
            `${BASE}/gateways/${gatewayId}/intent-routing/intents`,
            payload,
            CONNECTION
          )) as IntentRoutingConfig);
      const config = normalizeIntentRouting(data);
      logLlmGateway('app', intent.id ? 'intent_updated' : 'intent_created', { gatewayId, id: intent.id });
      return config;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'intent_save_failed', { gatewayId, id: intent.id, message });
      throw new Error(message);
    }
  },

  async deleteIntent(gatewayId: string, intentId: string): Promise<IntentRoutingConfig> {
    try {
      const data = (await apiService.delete(
        `${BASE}/gateways/${gatewayId}/intent-routing/intents/${intentId}`,
        CONNECTION
      )) as IntentRoutingConfig;
      const config = normalizeIntentRouting(data);
      logLlmGateway('warning', 'intent_deleted', { gatewayId, id: intentId });
      return config;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'intent_delete_failed', { gatewayId, id: intentId, message });
      throw new Error(message);
    }
  },

  async getIntentStatistics(
    gatewayId: string,
    range: { from: string; to: string }
  ): Promise<IntentGatewayStatistics> {
    try {
      const data = (await apiService.get(
        `${STATS_BASE}/gateways/${gatewayId}/statistics`,
        { from: range.from, to: range.to },
        CONNECTION
      )) as IntentGatewayStatistics;
      logLlmGateway('app', 'intent_stats_loaded', {
        gatewayId,
        requests: data?.totals?.requests || 0,
        matched: data?.totals?.matched || 0,
      });
      return data;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'intent_stats_failed', { gatewayId, message });
      throw new Error(message);
    }
  },

  async getIntentEvents(
    gatewayId: string,
    range: { from: string; to: string; limit?: number; offset?: number }
  ): Promise<IntentGatewayEvents> {
    const limit = range.limit ?? 100;
    const offset = range.offset ?? 0;
    try {
      const data = (await apiService.get(
        `${STATS_BASE}/gateways/${gatewayId}/events`,
        { from: range.from, to: range.to, limit, offset },
        CONNECTION
      )) as IntentGatewayEvents;
      logLlmGateway('app', 'intent_events_loaded', {
        gatewayId,
        total: data?.total || 0,
        limit: data?.limit ?? limit,
        offset: data?.offset ?? offset,
        count: data?.events?.length || 0,
      });
      return {
        gatewayId: data?.gatewayId || gatewayId,
        events: Array.isArray(data?.events) ? data.events : [],
        total: Number(data?.total) || 0,
        limit: Number(data?.limit) || limit,
        offset: Number(data?.offset) || offset,
      };
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'intent_events_failed', { gatewayId, message, limit, offset });
      throw new Error(message);
    }
  },

  async testConnection(id: string): Promise<{ ok: boolean; message: string }> {
    try {
      const data = (await apiService.post(`${BASE}/gateways/${id}/test-connection`, {}, CONNECTION)) as {
        ok?: boolean;
        message?: string;
      };
      logLlmGateway(data.ok ? 'app' : 'warning', 'test_connection', { id, ok: data.ok });
      return { ok: Boolean(data.ok), message: data.message || (data.ok ? 'Connection succeeded.' : 'Connection failed.') };
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'test_connection_failed', { message, id });
      throw new Error(message);
    }
  },
};
